import {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useLocalize } from '@core-ui/localization-provider';
import { TAG_LINK_GROUP_LABEL } from './localization-tokens';
import { customAttribute } from '@core-ui/reporting';
import { getRelatedSubCategories } from '../../adapters/getRelatedSubCategories';

const CUSTOM_ATTRIBUTE_SMART_CROSS_LINK = 'smartCrosslinkSource';

const RelatedCategoriesContext = createContext({});

const reportCrossLink = ({ containsCrossLinks, relatedLinks }) => {
  if (containsCrossLinks) {
    customAttribute(CUSTOM_ATTRIBUTE_SMART_CROSS_LINK, 'bloomReach');
    return;
  }

  customAttribute(
    CUSTOM_ATTRIBUTE_SMART_CROSS_LINK,
    relatedLinks?.length > 0
      ? 'webHierarchy (legacy)'
      : 'noData (componentFailure)'
  );
  return;
};

const RelatedCategoriesProvider = ({
  cid,
  smartCrosslinks,
  children,
  relatedCategoryLinks,
}) => {
  const { localize } = useLocalize();
  const [subCategoryState, setSubCategoryState] = useState({
    isSubCatPage: false,
    relatedSubCatLinks: [],
  });
  const containsCrossLinks = !!(smartCrosslinks?.crosslinks?.length > 0);

  const onFacetChange = useCallback(
    (facets) => {
      if (containsCrossLinks) {
        return;
      }
      const isSubCat = !!(facets?.appliedFacets?.style?.length > 0);
      setSubCategoryState({
        isSubCatPage: isSubCat,
        relatedSubCatLinks: isSubCat
          ? getRelatedSubCategories(facets, cid)
          : [],
      });
    },
    [cid, containsCrossLinks]
  );

  const getRelatedLinks = useCallback(() => {
    if (containsCrossLinks) {
      return smartCrosslinks.crosslinks;
    }
    if (subCategoryState.isSubCatPage) {
      return subCategoryState.relatedSubCatLinks;
    }
    return relatedCategoryLinks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategoryState]);

  const relatedLinks = useMemo(getRelatedLinks, [getRelatedLinks]);

  useEffect(
    () =>
      reportCrossLink({
        containsCrossLinks,
        tagLinkGroupLabel: localize(TAG_LINK_GROUP_LABEL),
        relatedLinks,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [relatedLinks]
  );

  return (
    <RelatedCategoriesContext.Provider
      value={{
        categories: relatedLinks,
        onFacetChange,
        customLabel: containsCrossLinks ? localize(TAG_LINK_GROUP_LABEL) : '',
      }}
    >
      {children}
    </RelatedCategoriesContext.Provider>
  );
};

const useRelatedCategories = () => {
  const context = useContext(RelatedCategoriesContext);

  if (!context) {
    throw new Error(
      'useRelatedCategories must be used within an RelatedCategoriesProvider'
    );
  }

  return context;
};

export { RelatedCategoriesProvider, useRelatedCategories };
